import React, { useState, useEffect } from 'react';
import { Play, Square, Timer } from 'lucide-react';
import { attendanceApi, AttendanceStatus } from '../../services/api/attendance';
import { mockAttendanceActions } from '../../services/mockData';
import { useAttendanceContext, AttendanceProvider } from '../../contexts/AttendanceContext';
import toast from 'react-hot-toast';

interface PunchButtonProps {
  userId?: string;
  compact?: boolean;
  className?: string;
  onStatusChange?: (status: AttendanceStatus) => void;
}

// Internal component that uses the context
const PunchButtonContent: React.FC<PunchButtonProps> = ({
  userId,
  compact = false,
  className = '',
  onStatusChange,
}) => {
  const [status, setStatus] = useState<AttendanceStatus | null>(null);
  const [loading, setLoading] = useState(false);

  // Get context for triggering updates
  const { updateLastAction, refreshTrigger } = useAttendanceContext();
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every minute for session duration display
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Fetch current status
  const fetchStatus = async () => {
    try {
      setLoading(true);
      const statusResponse = await attendanceApi.getCurrentStatus();
      setStatus(statusResponse);
      onStatusChange?.(statusResponse);
    } catch (error: any) {
      console.error('Error fetching punch status:', error);
      toast.error('Failed to load attendance status. Please try again.');
      setStatus(null);
      onStatusChange?.(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle clock in
  const handleClockIn = async () => {
    try {
      setLoading(true);

      const clockData = {
        source: 'web' as const,
        ...(userId && { userId }),
      };

      console.log('🔄 Starting clock in process...');
      await attendanceApi.clockInEnhanced(clockData);

      toast.success('Clocked in successfully!');
      console.log('✅ Clock in successful, refreshing status...');

      // Force refresh status after successful clock in
      await fetchStatus();

      // Trigger context update to refresh calendar and stats
      updateLastAction();

      // Also trigger onStatusChange callback if provided
      if (onStatusChange) {
        const newStatus = await attendanceApi.getCurrentStatus();
        onStatusChange(newStatus);
      }
    } catch (error: any) {
      console.error('❌ Clock in error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to clock in';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle clock out
  const handleClockOut = async () => {
    try {
      setLoading(true);

      const clockData = {
        source: 'web' as const,
        ...(userId && { userId }),
      };

      console.log('🔄 Starting clock out process...');
      await attendanceApi.clockOutEnhanced(clockData);

      toast.success('Clocked out successfully!');
      console.log('✅ Clock out successful, refreshing status...');

      // Force refresh status after successful clock out
      await fetchStatus();

      // Trigger context update to refresh calendar and stats
      updateLastAction();

      // Also trigger onStatusChange callback if provided
      if (onStatusChange) {
        const newStatus = await attendanceApi.getCurrentStatus();
        onStatusChange(newStatus);
      }
    } catch (error: any) {
      console.error('❌ Clock out error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to clock out';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('🔄 PunchButton: Refreshing status due to trigger change:', refreshTrigger);
    fetchStatus();
  }, [userId, refreshTrigger]); // Add refreshTrigger to dependencies

  // Format duration for display
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  if (!status) {
    return (
      <button
        disabled
        className={`px-3 py-2 bg-gray-100 text-gray-400 rounded-md text-sm font-medium cursor-not-allowed ${className}`}
      >
        <Timer className="h-4 w-4 animate-spin" />
        {!compact && <span className="ml-2">Loading...</span>}
      </button>
    );
  }

  // Show Clock Out button if user is currently clocked in
  if (status.isActive) {
    const sessionDuration = status.currentSession?.duration || 0;

    return (
      <button
        onClick={handleClockOut}
        disabled={loading || !status.canClockOut}
        className={`px-3 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center ${className}`}
        title={`Current session: ${formatDuration(sessionDuration)}`}
      >
        {loading ? (
          <Timer className="h-4 w-4 animate-spin" />
        ) : (
          <Square className="h-4 w-4" />
        )}
        {!compact && (
          <span className="ml-2">
            Clock Out {sessionDuration > 0 && `(${formatDuration(sessionDuration)})`}
          </span>
        )}
      </button>
    );
  }

  // Show Clock In button if user is not clocked in
  return (
    <button
      onClick={handleClockIn}
      disabled={loading || !status.canClockIn}
      className={`px-3 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center ${className}`}
      title={`Today's total: ${formatDuration(status.todayTotal)}`}
    >
      {loading ? (
        <Timer className="h-4 w-4 animate-spin" />
      ) : (
        <Play className="h-4 w-4" />
      )}
      {!compact && <span className="ml-2">Clock In</span>}
    </button>
  );
};

// Main component - now always uses the content component directly
// Context should be provided at a higher level (UserDashboardLayout, ClientDashboardLayout, etc.)
const PunchButton: React.FC<PunchButtonProps> = (props) => {
  return <PunchButtonContent {...props} />;
};

export default PunchButton;
